/**
 * /settings/profile — User profile + MFA management.
 *
 * Shows whether MFA is enabled for the current user. Provides:
 *   - "Set up MFA" → POST /auth/mfa/setup (with current_password) → QR + backup codes
 *   - TOTP confirm input → POST /auth/mfa/confirm
 *   - "Disable MFA" → POST /auth/mfa/disable (requires current TOTP code)
 *
 * The setup step prompts for the user's current password as a fresh-auth
 * check — a stolen long-lived JWT alone can't rotate the TOTP secret.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { User } from "@/lib/types";

export const Route = createFileRoute("/_auth/settings/profile")({
  component: ProfilePage,
});

interface MFASetupOut {
  provisioning_uri: string;
  qr_code_data_uri: string;
  backup_codes: string[];
}

function ProfilePage() {
  const qc = useQueryClient();

  const { data: me } = useQuery<User>({
    queryKey: ["me"],
    queryFn: () => api("/api/auth/me"),
    staleTime: 30_000,
  });

  // MFA setup flow state
  const [setupData, setSetupData] = useState<MFASetupOut | null>(null);
  const [setupPassword, setSetupPassword] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [disableCode, setDisableCode] = useState("");
  const [disableError, setDisableError] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);

  const setupMutation = useMutation({
    mutationFn: (currentPassword: string) =>
      api<MFASetupOut>("/api/auth/mfa/setup", {
        method: "POST",
        body: { current_password: currentPassword },
      }),
    onSuccess: (data) => {
      setSetupData(data);
      setSetupPassword("");
      setConfirmCode("");
      setSetupError(null);
    },
    onError: (err: Error) => {
      setSetupError(err.message ?? "Failed to start MFA setup");
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (code: string) =>
      api("/api/auth/mfa/confirm", {
        method: "POST",
        body: { totp_code: code },
      }),
    onSuccess: () => {
      setSetupData(null);
      setConfirmCode("");
      setConfirmError(null);
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (err: Error) => {
      setConfirmError(err.message ?? "Invalid TOTP code");
    },
  });

  const disableMutation = useMutation({
    mutationFn: (code: string) =>
      api("/api/auth/mfa/disable", {
        method: "POST",
        body: { totp_code: code },
      }),
    onSuccess: () => {
      setDisableCode("");
      setDisableError(null);
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (err: Error) => {
      setDisableError(err.message ?? "Invalid TOTP code");
    },
  });

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (!/^\d{6}$/.test(confirmCode)) {
      setConfirmError("Enter exactly 6 digits");
      return;
    }
    confirmMutation.mutate(confirmCode);
  }

  async function handleDisable(e: React.FormEvent) {
    e.preventDefault();
    if (!/^\d{6}$/.test(disableCode)) {
      setDisableError("Enter exactly 6 digits");
      return;
    }
    disableMutation.mutate(disableCode);
  }

  const mfaEnabled = me?.mfa_enabled ?? false;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-4 dark:bg-gray-900 dark:border-gray-800 flex-shrink-0">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Profile</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Account settings and security
        </p>
      </div>

      <div className="overflow-y-auto flex-1 px-6 py-6 bg-slate-50 dark:bg-gray-950">
        <div className="max-w-xl space-y-5">

          {/* Account info */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
              <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Account
              </h2>
            </div>
            <div className="px-5 py-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Email</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {me?.email ?? "—"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Name</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {me?.full_name ?? "—"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Role</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {me?.role ?? "—"}
                </span>
              </div>
            </div>
          </div>

          {/* MFA section */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Two-factor authentication
              </h2>
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  mfaEnabled
                    ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                    : "bg-slate-100 text-slate-500 dark:bg-gray-800 dark:text-slate-400"
                }`}
              >
                {mfaEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>

            <div className="px-5 py-4">
              {!mfaEnabled && !setupData && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!setupPassword) {
                      setSetupError("Enter your current password to start MFA setup.");
                      return;
                    }
                    setupMutation.mutate(setupPassword);
                  }}
                  className="space-y-3"
                >
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Add an extra layer of security with a TOTP authenticator app (Google
                    Authenticator, Authy, 1Password, etc.). Confirm your current password
                    to begin.
                  </p>
                  <input
                    type="password"
                    placeholder="Current password"
                    value={setupPassword}
                    onChange={(e) => setSetupPassword(e.target.value)}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700"
                    autoComplete="current-password"
                  />
                  {setupError && (
                    <p className="text-sm text-red-600">{setupError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={setupMutation.isPending || !setupPassword}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {setupMutation.isPending ? "Setting up…" : "Set up MFA"}
                  </button>
                </form>
              )}

              {!mfaEnabled && setupData && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Scan the QR code with your authenticator app, then enter the 6-digit
                    code to confirm.
                  </p>

                  {/* QR code */}
                  <div className="flex justify-center">
                    <img
                      src={setupData.qr_code_data_uri}
                      alt="MFA QR code"
                      className="w-44 h-44 rounded border border-slate-200 dark:border-gray-700"
                    />
                  </div>

                  {/* Manual entry link */}
                  <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
                    Can't scan?{" "}
                    <a
                      href={setupData.provisioning_uri}
                      className="underline hover:text-slate-600"
                    >
                      Open in authenticator app
                    </a>
                  </p>

                  {/* Backup codes */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2">
                      Backup codes — save these now, shown once only
                    </p>
                    <div className="grid grid-cols-2 gap-1">
                      {setupData.backup_codes.map((code) => (
                        <code
                          key={code}
                          className="text-xs font-mono bg-white dark:bg-gray-900 rounded px-2 py-1 text-amber-800 dark:text-amber-300"
                        >
                          {code}
                        </code>
                      ))}
                    </div>
                  </div>

                  {/* Confirm form */}
                  <form onSubmit={handleConfirm} className="flex gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="6-digit code"
                      value={confirmCode}
                      onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 tracking-widest text-center"
                      maxLength={6}
                      autoFocus
                    />
                    <button
                      type="submit"
                      disabled={confirmMutation.isPending}
                      className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {confirmMutation.isPending ? "Confirming…" : "Confirm"}
                    </button>
                  </form>
                  {confirmError && (
                    <p className="text-sm text-red-600">{confirmError}</p>
                  )}
                </div>
              )}

              {mfaEnabled && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    MFA is active on your account. Enter your current TOTP code to disable it.
                  </p>
                  <form onSubmit={handleDisable} className="flex gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="6-digit code"
                      value={disableCode}
                      onChange={(e) =>
                        setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                      }
                      className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 tracking-widest text-center"
                      maxLength={6}
                    />
                    <button
                      type="submit"
                      disabled={disableMutation.isPending}
                      className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {disableMutation.isPending ? "Disabling…" : "Disable MFA"}
                    </button>
                  </form>
                  {disableError && (
                    <p className="text-sm text-red-600">{disableError}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
